import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Checkbox,
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  DialogShellLayout,
  DialogShellNav,
  DialogShellSection,
  Label,
  Skeleton,
  cn,
  type DialogShellFooterAction,
  type DialogShellFooterProps
} from '@bliks-io/ui';
import {
  isSelectableDifference,
  selectableDifferenceIds,
  type DataSyncDifference,
  type DataSourceKind,
  type DataSyncRun
} from '@bliks-io/contracts';
import { useDataSyncRun } from '@/api/queries/useDataSync';
import { useApplyDataSync, useRetryDataSync } from '@/api/mutations/useDataSync';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { DialogLoadStateBody } from '@/components/dialogs/DialogLoadStateBody';
import { readDialogLoadState } from '@/lib/dialogLoadState';
import {
  SYNC_STEPS,
  canRetry,
  differencesOfStep,
  notApplied,
  selectAllState,
  selectedCount,
  setChecked,
  stepRowCount,
  unselectedDependencySteps,
  visibleSteps,
  type SyncStepId
} from './syncSelection';
import { SyncDifferenceRow, describeDifference, differenceText } from './SyncDifferenceRow';
import { SyncConfirmStep } from './SyncConfirmStep';

interface DataSyncDialogProps {
  runId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ApplySync {
  apply: (selectedDifferenceIds: string[]) => void;
  retry: () => void;
  isPending: boolean;
}

/** Shows a sync run step by step; a run in review applies the admin's selection. */
export function DataSyncDialog({ runId, open, onOpenChange }: DataSyncDialogProps) {
  const { t } = useTranslation();
  const runQuery = useDataSyncRun(runId, { enabled: open });
  const run = runQuery.data;
  const applyMutation = useApplyDataSync();
  const retryMutation = useRetryDataSync();
  const close = () => onOpenChange(false);

  const written = {
    onSuccess: (applied: DataSyncRun) => {
      // Otherwise the run, now done, shows the failed and conflicting rows.
      if (notApplied(applied).length === 0) {
        toast.success(t('dataSync.sync.applied'));
        close();
      }
    },
    onError: (error: unknown) => toastApiError(error, 'errors.unknown')
  };
  const applySync: ApplySync = {
    apply: (selectedDifferenceIds) =>
      applyMutation.mutate({ runId, selectedDifferenceIds }, written),
    retry: () => retryMutation.mutate({ runId }, written),
    isPending: applyMutation.isPending || retryMutation.isPending
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell size="lg" minHeight={560}>
        <DialogShellHeader
          title={t(`dataSync.sync.title.${run?.sourceKind ?? 'loading'}`)}
          onClose={close}
        />
        {run ? (
          // A status change (another admin applied, or this apply finished) re-initializes the view.
          <SyncRunView
            key={`${run.id}:${run.status}`}
            run={run}
            applySync={applySync}
            onClose={close}
          />
        ) : (
          <DialogShellBody>
            <DialogLoadStateBody
              loadState={readDialogLoadState([runQuery])}
              notFoundKey="errors.dataSync.syncRunNotFound"
              skeleton={[1, 2, 3, 4].map((row) => (
                <Skeleton key={row} className="h-10" />
              ))}
            />
          </DialogShellBody>
        )}
      </DialogShell>
    </Dialog>
  );
}

interface SyncRunViewProps {
  run: DataSyncRun;
  applySync: ApplySync;
  onClose: () => void;
}

function SyncRunView({ run, applySync, onClose }: SyncRunViewProps) {
  if (run.status === 'failed') return <SyncFailure run={run} onClose={onClose} />;
  if (run.status === 'done' && notApplied(run).length > 0) {
    return <SyncResult run={run} applySync={applySync} onClose={onClose} />;
  }
  return <SyncWizard run={run} applySync={applySync} onClose={onClose} />;
}

function SyncFailure({ run, onClose }: Omit<SyncRunViewProps, 'applySync'>) {
  const { t } = useTranslation();
  return (
    <>
      <DialogShellBody>
        <p role="alert" className="text-sm text-danger">
          {t(run.failureCode ?? 'errors.unknown')}
        </p>
      </DialogShellBody>
      <DialogShellFooter primary={{ label: t('common.close'), onClick: onClose }} />
    </>
  );
}

function SyncResult({ run, applySync, onClose }: SyncRunViewProps) {
  const { t } = useTranslation();

  return (
    <>
      <DialogShellBody>
        <DialogShellSection title={t('dataSync.sync.notApplied')}>
          <ul className="divide-y divide-line-subtle">
            {notApplied(run).map((difference) => {
              const outcome = run.outcomes[difference.id];
              return (
                <li key={difference.id} className="py-2.5">
                  <div className="text-sm font-medium text-foreground">
                    {describeDifference(difference, run.sourceKind, t).title}
                  </div>
                  <div
                    className={cn('text-xs', outcome === 'failed' ? 'text-danger' : 'text-warning')}
                  >
                    {t(`dataSync.sync.outcome.${outcome}`)}
                  </div>
                </li>
              );
            })}
          </ul>
        </DialogShellSection>
      </DialogShellBody>
      <DialogShellFooter
        secondary={{ label: t('common.close'), onClick: onClose }}
        primary={
          canRetry(run)
            ? {
                label: t('dataSync.sync.retry'),
                onClick: applySync.retry,
                loading: applySync.isPending
              }
            : undefined
        }
      />
    </>
  );
}

function SyncWizard({ run, applySync, onClose }: SyncRunViewProps) {
  const { t } = useTranslation();
  const readOnly = run.status !== 'review';
  // Read-only, the rows checked are those the apply wrote.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() =>
    readOnly ? new Set(Object.keys(run.outcomes)) : selectableDifferenceIds(run.differences)
  );
  const steps = visibleSteps(run);
  const [stepId, setStepId] = useState<SyncStepId>(steps[0]);
  const stepIndex = steps.indexOf(stepId);
  const step = SYNC_STEPS.find(({ id }) => id === stepId);
  const byStep = useMemo(
    () => new Map(SYNC_STEPS.map((entry) => [entry.id, differencesOfStep(run.differences, entry)])),
    [run.differences]
  );

  const toggle = useCallback(
    (targets: DataSyncDifference[], checked: boolean) =>
      setSelected((current) => setChecked(run.differences, current, targets, checked)),
    [run.differences]
  );

  const goTo = (index: number) => setStepId(steps[index]);
  const back: DialogShellFooterAction | undefined =
    stepIndex > 0 ? { label: t('common.back'), onClick: () => goTo(stepIndex - 1) } : undefined;

  const footerActions = (): Pick<DialogShellFooterProps, 'primary' | 'danger'> => {
    if (step)
      return { primary: { label: t('common.continue'), onClick: () => goTo(stepIndex + 1) } };
    if (readOnly) return { primary: { label: t('common.close'), onClick: onClose } };
    const apply: DialogShellFooterAction = {
      label: t('dataSync.sync.apply'),
      onClick: () =>
        applySync.apply(run.differences.filter(({ id }) => selected.has(id)).map(({ id }) => id)),
      loading: applySync.isPending
    };
    const destructive = SYNC_STEPS.some(
      (entry) => entry.destructive && selectedCount(run.differences, selected, entry) > 0
    );
    return destructive ? { danger: apply } : { primary: apply };
  };

  return (
    <>
      <DialogShellBody scroll={false}>
        <DialogShellLayout
          sidebarWidth={200}
          sidebar={
            <DialogShellNav
              aria-label={t('dataSync.sync.stepsLabel')}
              sections={[
                {
                  id: 'steps',
                  items: steps.map((id) => {
                    const entry = SYNC_STEPS.find((candidate) => candidate.id === id);
                    return {
                      id,
                      label: t(`dataSync.sync.steps.${id}`),
                      count: entry && stepRowCount(run, entry),
                      active: id === stepId,
                      onSelect: () => setStepId(id)
                    };
                  })
                }
              ]}
            />
          }
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            {step ? (
              <StepRows
                allDifferences={run.differences}
                differences={byStep.get(step.id) ?? []}
                sourceKind={run.sourceKind}
                selected={selected}
                readOnly={readOnly}
                onToggle={toggle}
              />
            ) : (
              <SyncConfirmStep run={run} selected={selected} />
            )}
          </div>
        </DialogShellLayout>
      </DialogShellBody>
      <DialogShellFooter secondary={back} {...footerActions()} />
    </>
  );
}

interface StepRowsProps {
  allDifferences: DataSyncDifference[];
  differences: DataSyncDifference[];
  sourceKind: DataSourceKind;
  selected: ReadonlySet<string>;
  readOnly: boolean;
  onToggle: (targets: DataSyncDifference[], checked: boolean) => void;
}

function StepRows({
  allDifferences,
  differences,
  sourceKind,
  selected,
  readOnly,
  onToggle
}: StepRowsProps) {
  const { t } = useTranslation();
  const texts = useMemo(
    () => differences.map((difference) => differenceText(difference, sourceKind, t)),
    [differences, sourceKind, t]
  );
  const selectable = useMemo(
    () => differences.filter((difference) => isSelectableDifference(difference, selected)),
    [differences, selected]
  );
  const selectableIds = useMemo(() => new Set(selectable.map(({ id }) => id)), [selectable]);
  const waitingOn = useMemo(() => {
    if (readOnly) return [];
    const byId = new Map(allDifferences.map((difference) => [difference.id, difference]));
    return differences.map((difference) => {
      const steps = unselectedDependencySteps(difference, byId, selected);
      return steps.length > 0
        ? t('dataSync.sync.waitingOn', {
            steps: steps.map((id) => t(`dataSync.sync.steps.${id}`)).join(', ')
          })
        : undefined;
    });
  }, [allDifferences, differences, selected, readOnly, t]);
  const selectAll = selectAllState(selectable, selected);

  return (
    <div className="flex flex-col">
      {!readOnly && differences.length > 0 && (
        <div className="flex items-center gap-3 border-b border-line-subtle pb-2.5">
          <Checkbox
            id="data-sync-select-all"
            checked={selectAll}
            disabled={selectable.length === 0}
            onCheckedChange={() => onToggle(differences, selectAll !== true)}
          />
          <Label htmlFor="data-sync-select-all">{t('common.selectAll')}</Label>
        </div>
      )}
      <ul className="divide-y divide-line-subtle">
        {differences.map((difference, index) => (
          <SyncDifferenceRow
            key={difference.id}
            difference={difference}
            text={texts[index]}
            waitingOn={waitingOn[index]}
            checked={selected.has(difference.id)}
            disabled={readOnly || !selectableIds.has(difference.id)}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </div>
  );
}
